import { DateTime } from "ts-luxon";
import { MILE, mulBaseUnits } from "@/util/miles";

export type AuctionType = "dutch-live" | "dutch-low-bid";

export type AuctionStatus =
  | "pending"
  | "active"
  | "cancelled"
  | "ended"
  | "complete";

/**
 * Bid lifecycle as reported by the API's `bid_status`.
 * `unfunded` and `failed` are won bids whose settlement charge did not go
 * through (insufficient miles, or some other error).
 */
export type BidStatus =
  | "active"
  | "cancelled"
  | "won"
  | "lost"
  | "unfunded"
  | "failed";

/** A bid as returned by the API. Amounts are base units (9 decimals). */
export interface ListingBid {
  /** Time-ordered uuid. Opaque: never parse or sort it numerically. */
  id: string;
  bid_amount: number;
  bid_quantity: number;
  created_at: string;
  special_auction_id?: string;
  blokpax_user_id?: string;
  /**
   * Always a string once the API schema is migrated; live dutch purchases
   * arrive as "won" + settled. Null means unknown, never "live purchase".
   */
  bid_status?: BidStatus | null;
  filled_quantity?: number | null;
  /** True once the settlement charge has been recorded. */
  settled?: boolean;
  /** Total actually charged for this bid, in base units. 0 until settled. */
  settled_amount?: number | null;
}

export interface PriceLevel {
  price: number;
  started_at: string;
  ended_at: string;
}

export interface ListingData {
  id: number;
  slug: string;
  title: string;
  subtitle: string;
  description: string;
  short_description: string;
  starts_at: string;
  visible_at?: string;
  /** Scheduled end. Only meaningful for dutch-low-bid auctions. */
  ends_at?: string | null;
  /** Per-unit price every winner pays. Present once a low-bid auction ends. */
  clearing_price?: number | null;
  auction_type?: AuctionType;
  auction_status: AuctionStatus;
  buy_limit?: number;
  buy_limit_scope?: string;
  user_buy_limit?: null | number;
  /** Floor price per unit for dutch-low-bid auctions. */
  min_bid?: number | null;
  current_price: PriceLevel | null;
  images: string[];
  /** Present on the bids endpoints only. */
  /** The authenticated caller's own bids, newest first. `[]` when anonymous; absent on the list endpoint. */
  bids?: ListingBid[];
}

export class BaseClass<T> {
  public data: T;

  constructor(data: any) {
    this.data = data;
    return makeProxy(this);
  }
}

function makeProxy(obj: any) {
  return new Proxy(obj, {
    get(target, prop) {
      if (prop in target) {
        let t = target[prop];

        if (typeof t === "function") {
          return (...args: any) => {
            return t.apply(target, args);
          };
        }

        return t;
      }

      return target.data[prop];
    },
  });
}

const TERMINAL_STATUSES: AuctionStatus[] = ["ended", "complete", "cancelled"];

export interface BidSummary {
  /** Bids that were in play at close (excludes cancelled). */
  totalBids: number;
  wonBids: number;
  wonUnits: number;
  /** Base units, as a decimal string. */
  milesPaid: string;
}

// BaseClass proxies unknown properties through to `data`; merging the data
// interface into the class type makes `listing.title` etc. type-check.
export interface Listing extends ListingData {}

export class Listing extends BaseClass<ListingData> {
  auctionType(): AuctionType {
    return this.data.auction_type ?? "dutch-live";
  }

  isLowBid(): boolean {
    return this.auctionType() === "dutch-low-bid";
  }

  /**
   * Floor price per unit in base units.
   *
   * Low-bid (lowest clearing price) auctions default to 1 mile when the API
   * supplies no floor. Live auctions have no floor until a price tier exists.
   */
  minBid(): number | null {
    const floor = this.data.min_bid ?? this.data.current_price?.price ?? null;
    if (floor === null && this.isLowBid()) {
      return MILE;
    }
    return floor;
  }

  /**
   * True when an admin set a public floor for this auction. The API omits
   * `min_bid` otherwise, even though it still enforces the global 1 mile floor.
   */
  hasPublicFloor(): boolean {
    return this.data.min_bid != null;
  }

  isActive() {
    if (this.isLowBid()) {
      return this.startsAt() <= DateTime.now() && !this.hasEnded();
    }

    const starts = this.startsAt();
    const ends = this.endsAt();

    return starts < new Date() && ends && ends > new Date();
  }

  hasEnded() {
    if (this.isLowBid()) {
      if (TERMINAL_STATUSES.includes(this.data.auction_status)) {
        return true;
      }

      const ends = this.auctionEndsAt();
      return !!ends && ends <= DateTime.now();
    }

    const ended = this.startsAt() < new Date() && !this.endsAt();
    return ended;
  }

  /** Settlement has run; bid statuses are final. */
  isSettled(): boolean {
    return this.data.auction_status === "complete";
  }

  startsAt() {
    return DateTime.fromISO(this.data.starts_at);
  }

  /** Scheduled auction end (low-bid only). */
  auctionEndsAt(): DateTime | null {
    return this.data.ends_at ? DateTime.fromISO(this.data.ends_at) : null;
  }

  endsAt() {
    if (this.isLowBid()) {
      return this.auctionEndsAt();
    }

    if (this.data.current_price) {
      return DateTime.fromISO(this.data.current_price.ended_at);
    }

    return null;
  }

  /**
   * What the page counts down to: the start while pending, the auction end
   * for low-bid, otherwise the end of the current price tier.
   */
  countdownTarget(): DateTime | null {
    const starts = this.startsAt();
    if (starts > DateTime.now()) {
      return starts;
    }

    if (this.isLowBid()) {
      return this.auctionEndsAt();
    }

    if (this.data.current_price) {
      return DateTime.fromISO(this.data.current_price.ended_at);
    }

    return null;
  }

  allBids(): ListingBid[] {
    return this.data.bids ?? [];
  }

  /** Active bids, highest unit bid first; newest first when amounts tie. */
  activeBids(): ListingBid[] {
    return this.allBids()
      .filter((bid) => bid.bid_status === "active")
      .sort(byUnitBidDesc);
  }

  bidSummary(): BidSummary {
    return summarizeBids(this.allBids(), this.data.clearing_price ?? null);
  }
}

/** Highest unit bid first; newest first when amounts tie. */
export function byUnitBidDesc(a: ListingBid, b: ListingBid): number {
  return b.bid_amount - a.bid_amount || b.created_at.localeCompare(a.created_at);
}

/**
 * Aggregate the current user's own bids after settlement.
 *
 * Miles paid comes from the recorded charge once a bid is settled, and is
 * otherwise estimated from the auction's clearing price.
 */
export function summarizeBids(
  bids: ListingBid[],
  clearingPrice: number | null = null,
): BidSummary {
  const inPlay = bids.filter((bid) => bid.bid_status !== "cancelled");
  const won = inPlay.filter((bid) => bid.bid_status === "won");

  let milesPaid = 0n;
  let wonUnits = 0;
  for (const bid of won) {
    const filled = bid.filled_quantity ?? 0;
    wonUnits += filled;
    milesPaid += bid.settled
      ? BigInt(Math.floor(bid.settled_amount ?? 0))
      : BigInt(mulBaseUnits(clearingPrice ?? 0, filled));
  }

  return {
    totalBids: inPlay.length,
    wonBids: won.length,
    wonUnits,
    milesPaid: milesPaid.toString(),
  };
}

/** A bid the user is about to place (client-side only). */
export interface Bid {
  amount: number;
  quantity: number;
}

export * as Errors from "./errors";
